import { useState } from 'react';
import styled from '@emotion/styled';
import CardIcon from '@/components/Icon/CardIcon';
import MoneyIcon from '@/components/Icon/MoneyIcon';
import CardIconActive from '@/components/Icon/CardIconActive';
import MoneyIconActive from '@/components/Icon/MoneyIconActive';
import Field from '../../Field/Field';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePostCreateTransferInfoApi, usePostPaymentApprovalCallbackApi, usePostPaymentReadyApi } from '@/api/payment';
import { convertPaymentRegisterValue, convertPaymentValue } from '@/utils/convertValue';
import dayjs from 'dayjs';
import { CONF_TITLE } from '@/constants/menu';

interface RegisterField {
	payment: string;
	member: string;
	// cardNumber?: { first: string; second: string; third: string; fourth: string };
	// periodDate?: { month: string; year: string };
	// owner?: string;
	// payMonth?: string;
}
interface PaymentModalProps {
	closeModal: (bool: boolean) => void;
	goodsName: string;
	memberType?: string;
}
const PaymentModal = ({ closeModal, goodsName, memberType }: PaymentModalProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const year = dayjs().format('YYYY');
	const userRegisterSchema = z.object({
		payment: z.string(),
		member: z.string(),
	});

	const { register, handleSubmit, watch, setValue } = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			payment: 'card',
			member: memberType,
		},
	});

	const { mutateAsync: paymentReady } = usePostPaymentReadyApi();
	const { mutateAsync: paymentApprovalCallback } = usePostPaymentApprovalCallbackApi();
	const { mutateAsync: createTransferInfo } = usePostCreateTransferInfoApi();
	const handleModalClick = (payment: string) => {
		setValue('payment', payment);
	};
	const onSubmit = async (_: RegisterField) => {
		if (isOpen) {
			closeModal(false);
		}
		if (watch('payment') === 'account' && !isOpen) {
			const res = await createTransferInfo({
				member_type: memberType ? memberType : watch('member'),
				amount:
					goodsName === '연회비' ? convertPaymentValue(watch('member')).replace(/,/g, '') : convertPaymentRegisterValue(watch('member')).replace(/,/g, ''),
				goods_type: goodsName === '연회비' ? '연회비' : '등록비',
				goods_name: goodsName === '연회비' ? `${year}년도 연회비` : CONF_TITLE,
			});
			if (res.data?.success) {
				setIsOpen(true);
				return;
			}
			return;
		}
		if (watch('payment') === 'card') {
			await paymentReady({
				member_type: memberType ? memberType : watch('member'),
				amount:
					goodsName === '연회비'
						? Number(convertPaymentValue(watch('member')).replace(/,/g, ''))
						: Number(convertPaymentRegisterValue(watch('member')).replace(/,/g, '')),
				goods_type: goodsName === '연회비' ? '연회비' : '등록비',
				goods_name: goodsName === '연회비' ? `${year}년도 연회비` : CONF_TITLE,
			});
		}
	};

	return (
		<PaymentContainer onSubmit={handleSubmit(onSubmit)}>
			<Title>{isOpen ? '무통장 입금' : goodsName === '연회비' ? '연회비 납부' : '학회등록비 납부'}</Title>

			{!isOpen && (
				<>
					{goodsName === '연회비' ? (
						<Field title="회원구분" gap="0.8rem">
							<GenderBox>
								<GenderRadio>
									<input type="radio" value="정회원" {...register('member')} />
									정회원
								</GenderRadio>
								<GenderRadio>
									<input type="radio" value="학생회원" {...register('member')} />
									학생회원
								</GenderRadio>
							</GenderBox>
						</Field>
					) : (
						<AmountBox>
							<div>결제 금액</div>
							<div className="point">
								<strong>{goodsName === '연회비' ? convertPaymentValue(watch('member')) : convertPaymentRegisterValue(watch('member'))}</strong> 원
							</div>
						</AmountBox>
					)}

					<Field title="결제 수단" gap="0.8rem">
						<PaymentContetnBox>
							<PaymentContent className={watch('payment') === 'card' ? 'active' : ''} onClick={() => handleModalClick('card')}>
								{watch('payment') === 'card' ? <CardIconActive /> : <CardIcon />} 카드결제
							</PaymentContent>
							<PaymentContent className={watch('payment') === 'account' ? 'active' : ''} onClick={() => handleModalClick('account')}>
								{watch('payment') === 'account' ? <MoneyIconActive /> : <MoneyIcon />} 무통장 입금
							</PaymentContent>
						</PaymentContetnBox>
					</Field>
				</>
			)}
			{isOpen && (
				<PaymentAccountBox>
					{goodsName === '연회비' && (
						<div className="item">
							<p className="title">회원구분</p>
							<p className="content point">
								<p className="content">{watch('member')}</p>
							</p>
						</div>
					)}

					<div className="item">
						<p className="title">입금금액</p>
						<p className="content point">
							<strong className="point_bold">
								{goodsName === '연회비' ? convertPaymentValue(watch('member')) : convertPaymentRegisterValue(watch('member'))}
							</strong>
							원
						</p>
					</div>
					<div className="item">
						<p className="title">은행계좌</p>
						<p className="content">
							하나은행&nbsp;
							<strong className="point_bold">748-910007-79804</strong>
						</p>
					</div>
					<div className="item">
						<p className="title">예금주</p>
						<p className="content">한국노화학회</p>
					</div>
				</PaymentAccountBox>
			)}

			<PaymentButtonBox>
				{!isOpen && (
					<CancelButton type="button" onClick={() => closeModal(false)}>
						취소
					</CancelButton>
				)}
				<PaymentConfirmButton type="submit">{!isOpen ? '결제' : '확인'}</PaymentConfirmButton>
			</PaymentButtonBox>
		</PaymentContainer>
	);
};

export default PaymentModal;

// ✅ 스타일 정의

const PaymentContainer = styled.form`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 2rem;
`;
const Title = styled.h2`
	font-size: ${(props) => props.theme.fontSize.sizeXl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 130%;
	letter-spacing: -0.6px;
	color: #212529;
`;
const PaymentTitle = styled.div`
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	/* color: #212529; */
	letter-spacing: -0.6px;
	line-height: 130%;
	&.top {
		margin-top: 2rem;
	}
`;

const AmountBox = styled(PaymentTitle)`
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	.point {
		color: ${(props) => props.theme.colors.primary400};
		font-size: ${(props) => props.theme.fontSize.sizeLg};
		font-weight: ${(props) => props.theme.fontWeight.weightRegular};
		strong {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
		}
	}
`;

const PaymentButtonBox = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	gap: 1.2rem;
`;
const PaymentConfirmButton = styled.button`
	background-color: ${(props) => props.theme.colors.primary400};
	color: #ffffff;
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	letter-spacing: -0.6px;
	line-height: 100%;
	width: 100%;
	padding: 0rem 1.2rem;
	height: 4.4rem;
	border: none;
	cursor: pointer;
	&:disabled {
		background-color: #e9ecef;
		color: #868e96;
		cursor: not-allowed;
	}
`;
const CancelButton = styled(PaymentConfirmButton)`
	background-color: #ced4da;
	color: #212529;
`;

const PaymentContetnBox = styled.div`
	display: flex;
	gap: 0.8rem;
	width: 100%;
`;
const PaymentContent = styled.div`
	display: flex;
	height: 7.2rem;
	align-items: center;
	justify-content: center;
	width: 100%;
	gap: 0.8rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 120%;
	letter-spacing: -0.6px;
	color: #868e96;
	background-color: #fff;
	padding: 0rem 1.2rem 0rem 0.8rem;
	border: 1px solid #dee2e6;
	cursor: pointer;

	&.active {
		border: 2px solid ${(props) => props.theme.colors.primary400};
		color: ${(props) => props.theme.colors.primary400};
	}
`;
export const GenderBox = styled.div`
	display: flex;
	gap: 4rem;
`;

export const GenderRadio = styled.div`
	display: flex;
	gap: 0.4rem;
	align-items: center;
	input[type='radio'] {
		-webkit-appearance: none; // 웹킷 브라우저에서 기본 스타일 제거
		-moz-appearance: none; // 모질라 브라우저에서 기본 스타일 제거
		appearance: none; // 기본 브라우저에서 기본 스타일 제거
		width: 20px;
		height: 20px;
		border: 1px solid #ced4da; // 체크되지 않았을 때의 테두리 색상
		border-radius: 50%;
		outline: none; // focus 시에 나타나는 기본 스타일 제거
		cursor: pointer;
	}
	// 체크 시 버튼 모양 스타일
	input[type='radio']:checked {
		background-color: #fff; // 체크 시 내부 원 색상
		border: 6px solid ${(props) => props.theme.colors.primary400}; // 라인이 아닌, 라인과 원 사이 색상
	}
`;

export const PaymentAccountBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	background-color: #fff;
	.item {
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
		.title {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
			line-height: 130%;
			letter-spacing: -0.6px;
			color: #212529;
		}
		.content {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightRegular};
			line-height: 130%;
			letter-spacing: -0.6px;
			color: #212529;
		}
		.point_bold {
			color: ${(props) => props.theme.colors.primary400};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
		}
		.point {
			color: ${(props) => props.theme.colors.primary400};
		}
	}
`;
